import Chart from 'chart.js/auto';
import {T} from '../../shared/styles/tokens.js';

function enlargeTypography(chart) {
  const restore=[];
  function font(holder,key,emphasis=false) {
    const previous=holder[key],present=Object.hasOwn(holder,key);
    holder[key]=context=>({...((typeof previous==='function'?previous(context):previous)||{}),family:T.fontUi,
      size:Number.parseFloat(context.chart.width>=700&&emphasis?T.tBody:T.tSmall)*
        Number.parseFloat(getComputedStyle(chart.canvas.ownerDocument.documentElement).fontSize)});
    restore.push(()=>{if(present)holder[key]=previous;else delete holder[key];});
  }
  const options=chart.config.options;
  for(const scale of Object.values(options.scales||{}))if(scale.ticks)font(scale.ticks,'font');
  const plugins=options.plugins||{};
  if(plugins.datalabels&&typeof plugins.datalabels==='object')font(plugins.datalabels,'font',true);
  if(plugins.legend?.labels)font(plugins.legend.labels,'font');
  if(plugins.tooltip&&typeof plugins.tooltip==='object'){font(plugins.tooltip,'titleFont',true);font(plugins.tooltip,'bodyFont');}
  for(const dataset of chart.data.datasets)if(dataset.datalabels&&typeof dataset.datalabels==='object')font(dataset.datalabels,'font',true);
  chart.update('none');
  return ()=>{restore.forEach(reset=>reset());if(chart.canvas)chart.update('none');};
}

// Move the live card, rather than copying its canvas: data, callbacks, independent
// axes and any chart controls remain the same in both views.
export function installChartSpotlight(root) {
  const document = root.ownerDocument, window = document.defaultView;
  let active, disposed = false;
  const buttons = new Set();
  const resize = card => window.requestAnimationFrame(() => {
    if (disposed || !card.isConnected) return;
    card.querySelectorAll('canvas').forEach(canvas => Chart.getChart(canvas)?.resize());
    window.dispatchEvent(new Event('resize'));
  });
  function typography() {
    if(!active)return;
    active.card.querySelectorAll('canvas').forEach(canvas=>{
      const chart=Chart.getChart(canvas);
      if(chart&&!active.charts.has(chart)){active.charts.add(chart);active.restore.push(enlargeTypography(chart));}
    });
  }
  function close() {
    if (!active) return;
    const {card, placeholder, dialog, button, plots, overflow, restore} = active;
    active = null;
    if (dialog.open) dialog.close();
    plots.forEach(plot => plot.classList.remove('dashboard-spotlight-plot'));
    restore.forEach(reset=>reset());
    if (placeholder.isConnected) placeholder.replaceWith(card);
    else card.remove();
    dialog.remove();
    document.documentElement.style.overflow = overflow;
    if (button.isConnected && !disposed) button.focus({preventScroll:true});
    resize(card);
  }
  function open(card, button) {
    close();
    const placeholder = document.createElement('div');
    placeholder.className = card.className;
    placeholder.dataset.chartWidth = card.dataset.chartWidth || '';
    placeholder.style.height = card.getBoundingClientRect().height + 'px';
    placeholder.setAttribute('aria-hidden', 'true');
    card.before(placeholder);
    const dialog = document.createElement('dialog');
    dialog.className = 'o-modal dashboard-chart-spotlight';
    dialog.setAttribute('aria-label', 'Chart spotlight');
    const header = document.createElement('div');header.className = 'o-modal-head';
    const title = document.createElement('h2');title.textContent = 'Chart spotlight';
    const dismiss = document.createElement('button');
    dismiss.type = 'button';dismiss.className = 'o-btn o-btn-sm';dismiss.textContent = 'Close';
    dismiss.setAttribute('aria-label', 'Close chart spotlight');dismiss.onclick = close;
    const body = document.createElement('div');body.className = 'o-modal-body';
    header.append(title,dismiss);dialog.append(header,body);
    // Keep the original section ancestry and scoped chart styling.
    placeholder.after(dialog);body.append(card);
    const plots = [...new Set([...card.querySelectorAll('canvas')].map(canvas => canvas.parentElement))];
    plots.forEach(plot => plot.classList.add('dashboard-spotlight-plot'));
    active = {card,placeholder,dialog,button,plots,restore:[],charts:new Set(),overflow:document.documentElement.style.overflow};
    document.documentElement.style.overflow = 'hidden';
    dialog.addEventListener('cancel', event => {event.preventDefault();close();});
    dialog.addEventListener('close', () => {if(active?.dialog===dialog)close();});
    // Legacy page shortcuts consume Escape; handle it before it reaches them.
    dialog.addEventListener('keydown', event => {if(event.key==='Escape'){event.preventDefault();event.stopPropagation();close();}});
    let outside = false;
    const beyond = event => {const r=dialog.getBoundingClientRect();return event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom;};
    dialog.addEventListener('pointerdown', event => {outside=event.target===dialog&&beyond(event);});
    dialog.addEventListener('click', event => {if(outside&&event.target===dialog&&beyond(event))close();outside=false;});
    dialog.showModal();dismiss.focus();typography();resize(card);
  }
  function enhance() {
    for(const button of buttons)if(!button.isConnected)buttons.delete(button);
    if (active && !active.placeholder.isConnected) close();
    typography();
    root.querySelectorAll('canvas').forEach(canvas => {
      const card=canvas.closest('.card,.ch-card');
      if (!card) return;
      const existing=card.querySelector('[data-chart-spotlight]');
      if(existing){
        const name=card.querySelector('.card-title,.ch-name')?.textContent.trim()||'chart';
        existing.setAttribute('aria-label','Spotlight '+name);existing.title='Spotlight '+name;return;
      }
      const head=card.querySelector('.card-hdr,.card-header');
      if (!head) return;
      const button=document.createElement('button');button.type='button';
      button.className='o-btn o-btn-sm dashboard-spotlight-trigger';button.dataset.chartSpotlight='';
      const icon=document.createElement('i');icon.className='ti ti-maximize';icon.setAttribute('aria-hidden','true');button.append(icon);
      button.setAttribute('aria-haspopup','dialog');
      const name=card.querySelector('.card-title,.ch-name')?.textContent.trim()||'chart';
      button.setAttribute('aria-label','Spotlight '+name);button.title='Spotlight '+name;button.onclick=()=>open(card,button);
      let actions=head.querySelector('.dashboard-chart-actions');
      if(!actions){actions=document.createElement('div');actions.className='dashboard-chart-actions';head.append(actions);}
      actions.append(button);buttons.add(button);
    });
  }
  const observer=new MutationObserver(enhance);
  observer.observe(root,{childList:true,subtree:true});enhance();
  return () => {disposed=true;observer.disconnect();close();buttons.forEach(button=>button.remove());};
}
